
import React, { useState } from 'react';
import { IconUserGroup, IconPhone, IconThumbUp, IconSparkles, IconMail } from '../components/Icons';
import { generateInvestorUpdate } from '../services/geminiService';
import { useAiQ } from '../state/AiQState';
import toast from 'react-hot-toast';
import { Plus, CheckSquare, Square, Clock, Calendar } from 'lucide-react';

interface Task {
    id: string;
    text: string;
    done: boolean;
    due?: string;
}

interface Note {
    id: string;
    text: string;
    timestamp: string;
}

interface Client {
    id: string;
    name: string;
    aum: number;
    sentiment: 'Happy' | 'Neutral' | 'At Risk';
    lastContact: string;
    email: string;
    type: 'Family Office' | 'Pension Fund' | 'Sovereign Wealth' | 'Endowment' | 'HNW';
    tasks: Task[];
    notes: Note[];
}

const initClients: Client[] = [
    {
        id: 'CL-001',
        name: 'Highland Capital Family Office',
        aum: 50_000_000,
        sentiment: 'Happy',
        lastContact: '2 days ago',
        email: 'cio@highland.com',
        type: 'Family Office',
        tasks: [
            { id: 't1', text: 'Send Q3 performance report', done: false, due: '2024-10-15' },
            { id: 't2', text: 'Schedule annual review call', done: false },
        ],
        notes: [
            { id: 'n1', text: 'Interested in increasing tech allocation to 25%', timestamp: '2 days ago' },
            { id: 'n2', text: 'Concerned about rate sensitivity in bond sleeve', timestamp: '1 week ago' },
        ],
    },
    {
        id: 'CL-002',
        name: 'BlueOcean Pension Fund',
        aum: 25_000_000,
        sentiment: 'Neutral',
        lastContact: '1 week ago',
        email: 'inv_rel@blueocean.org',
        type: 'Pension Fund',
        tasks: [
            { id: 't3', text: 'Prepare liability-matching proposal', done: true },
            { id: 't4', text: 'Follow up on ESG screening request', done: false },
        ],
        notes: [
            { id: 'n3', text: 'Board meeting next month — wants full attribution report', timestamp: '1 week ago' },
        ],
    },
    {
        id: 'CL-003',
        name: 'Apex Sovereign Wealth',
        aum: 150_000_000,
        sentiment: 'At Risk',
        lastContact: '3 weeks ago',
        email: 'allocator@apex.gov',
        type: 'Sovereign Wealth',
        tasks: [
            { id: 't5', text: 'URGENT: Schedule re-engagement call', done: false, due: '2024-10-10' },
            { id: 't6', text: 'Prepare drawdown analysis for last quarter', done: false },
        ],
        notes: [
            { id: 'n4', text: 'No response to last 2 emails — high churn risk', timestamp: '3 weeks ago' },
        ],
    },
];

const sentimentColor = (s: Client['sentiment']) =>
    s === 'Happy' ? { bg: 'bg-emerald-100', text: 'text-emerald-700' } :
    s === 'At Risk' ? { bg: 'bg-red-100', text: 'text-red-700' } :
    { bg: 'bg-slate-100', text: 'text-slate-600' };

const CRMView: React.FC = () => {
    const { portfolioStats } = useAiQ();
    const [clients, setClients] = useState<Client[]>(initClients);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [emailDraft, setEmailDraft] = useState('');
    const [isDrafting, setIsDrafting] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [newTask, setNewTask] = useState('');
    const [activeDetailTab, setActiveDetailTab] = useState<'overview' | 'tasks' | 'notes' | 'outreach'>('overview');

    const selected = clients.find(c => c.id === selectedId) ?? null;

    const updateClient = (id: string, patch: Partial<Client>) =>
        setClients(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));

    const handleDraftEmail = async (client: Client) => {
        setIsDrafting(true);
        setEmailDraft('');
        const perfData = {
            monthlyReturn: (portfolioStats.dailyPnL / (portfolioStats.totalValuation || 1) * 30).toFixed(2),
            winner: 'NVIDIA (NVDA)',
            loser: 'US Treasuries',
            outlook: 'Cautiously optimistic on Tech, hedging Rates.',
        };
        const text = await generateInvestorUpdate(client.name, perfData);
        setEmailDraft(text);
        setIsDrafting(false);
    };

    const handleSend = () => {
        toast.success(`Update sent to ${selected?.email}`);
        setEmailDraft('');
    };

    const addNote = () => {
        if (!selected || !newNote.trim()) return;
        const note: Note = { id: `n${Date.now()}`, text: newNote.trim(), timestamp: 'Just now' };
        updateClient(selected.id, { notes: [note, ...selected.notes] });
        setNewNote('');
    };

    const addTask = () => {
        if (!selected || !newTask.trim()) return;
        const task: Task = { id: `t${Date.now()}`, text: newTask.trim(), done: false };
        updateClient(selected.id, { tasks: [...selected.tasks, task] });
        setNewTask('');
    };

    const toggleTask = (taskId: string) => {
        if (!selected) return;
        updateClient(selected.id, {
            tasks: selected.tasks.map(t => t.id === taskId ? { ...t, done: !t.done } : t),
        });
    };

    const pendingTasks = clients.reduce((sum, c) => sum + c.tasks.filter(t => !t.done).length, 0);
    const atRiskCount = clients.filter(c => c.sentiment === 'At Risk').length;

    return (
        <div className="flex h-full bg-[#F0F2F5]">
            {/* ── Left: Client list ── */}
            <div className="w-72 bg-white border-r border-slate-200 flex flex-col shrink-0">
                <div className="px-5 py-4 border-b border-slate-100">
                    <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                        <IconUserGroup /> Investor Relations
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">Client CRM & Capital Raising</p>

                    {/* Mini stats */}
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        <div className="bg-slate-50 rounded-lg p-2 text-center">
                            <div className="text-base font-bold text-slate-800">{clients.length}</div>
                            <div className="text-[10px] text-slate-400">Clients</div>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-2 text-center">
                            <div className="text-base font-bold text-amber-700">{pendingTasks}</div>
                            <div className="text-[10px] text-amber-500">Open Tasks</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-2 text-center">
                            <div className="text-base font-bold text-red-600">{atRiskCount}</div>
                            <div className="text-[10px] text-red-400">At Risk</div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {clients.map(client => {
                        const sc = sentimentColor(client.sentiment);
                        const openTasks = client.tasks.filter(t => !t.done).length;
                        return (
                            <div
                                key={client.id}
                                onClick={() => { setSelectedId(client.id); setActiveDetailTab('overview'); }}
                                className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
                                    selectedId === client.id
                                        ? 'bg-blue-50 border-blue-400 shadow-sm'
                                        : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                }`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className="font-semibold text-sm text-slate-800 leading-snug pr-2">{client.name}</span>
                                    <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-bold ${sc.bg} ${sc.text}`}>
                                        {client.sentiment}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-xs text-slate-500 mt-1">
                                    <span className="font-medium">${(client.aum / 1_000_000).toFixed(0)}M AUM</span>
                                    <span className="flex items-center gap-1">
                                        <Clock size={10} /> {client.lastContact}
                                    </span>
                                </div>
                                {openTasks > 0 && (
                                    <div className="mt-1.5 text-[10px] font-semibold text-amber-600 flex items-center gap-1">
                                        <CheckSquare size={10} /> {openTasks} open task{openTasks > 1 ? 's' : ''}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Right: Client detail ── */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {!selected ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 opacity-60 gap-3">
                        <IconUserGroup />
                        <p>Select an investor to view their profile</p>
                    </div>
                ) : (
                    <>
                        {/* Client header */}
                        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">{selected.name}</h1>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                                    <span className={`px-2 py-0.5 rounded font-bold ${sentimentColor(selected.sentiment).bg} ${sentimentColor(selected.sentiment).text}`}>
                                        {selected.sentiment}
                                    </span>
                                    <span>{selected.type}</span>
                                    <span className="flex items-center gap-1"><IconMail /> {selected.email}</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-slate-900 font-mono">${(selected.aum / 1_000_000).toFixed(0)}M</div>
                                <div className="text-xs text-slate-400 mt-0.5">AUM</div>
                            </div>
                        </div>

                        {/* Sub-tabs */}
                        <div className="bg-white border-b border-slate-200 px-6 flex items-center gap-0 shrink-0">
                            {(['overview', 'tasks', 'notes', 'outreach'] as const).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveDetailTab(tab)}
                                    className={`px-4 py-3 text-xs font-semibold capitalize border-b-2 transition-colors ${
                                        activeDetailTab === tab
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-slate-500 hover:text-slate-800'
                                    }`}
                                >
                                    {tab}
                                    {tab === 'tasks' && selected.tasks.filter(t => !t.done).length > 0 && (
                                        <span className="ml-1.5 bg-amber-100 text-amber-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                                            {selected.tasks.filter(t => !t.done).length}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* OVERVIEW TAB */}
                            {activeDetailTab === 'overview' && (
                                <div className="grid grid-cols-2 gap-5 max-w-3xl">
                                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                                        <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
                                            <IconPhone /> Recent Activity
                                        </h3>
                                        <div className="space-y-3">
                                            {selected.notes.slice(0, 3).map(note => (
                                                <div key={note.id} className="flex gap-3 text-sm">
                                                    <div className="w-1.5 h-1.5 mt-1.5 rounded-full bg-blue-400 shrink-0" />
                                                    <div>
                                                        <p className="text-slate-700 leading-snug">{note.text}</p>
                                                        <p className="text-xs text-slate-400 mt-0.5">{note.timestamp}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {selected.notes.length === 0 && (
                                                <p className="text-xs text-slate-400 italic">No notes yet</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                                        <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
                                            <IconThumbUp /> Sentiment
                                        </h3>
                                        <div className="space-y-2">
                                            {(['Happy', 'Neutral', 'At Risk'] as const).map(s => {
                                                const sc = sentimentColor(s);
                                                return (
                                                    <button
                                                        key={s}
                                                        onClick={() => updateClient(selected.id, { sentiment: s })}
                                                        className={`w-full py-2 rounded-lg text-xs font-bold transition-all border-2 ${
                                                            selected.sentiment === s
                                                                ? `${sc.bg} ${sc.text} border-current`
                                                                : 'bg-slate-50 text-slate-400 border-transparent hover:border-slate-200'
                                                        }`}
                                                    >
                                                        {s}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {/* Open tasks summary */}
                                    <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-5">
                                        <h3 className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
                                            <Calendar size={14} /> Upcoming Tasks
                                        </h3>
                                        {selected.tasks.filter(t => !t.done).length === 0 ? (
                                            <p className="text-xs text-slate-400 italic">All tasks complete</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {selected.tasks.filter(t => !t.done).map(task => (
                                                    <div key={task.id} className="flex items-center gap-2 text-sm text-slate-700">
                                                        <button onClick={() => toggleTask(task.id)} className="text-slate-400 hover:text-emerald-500 transition-colors shrink-0">
                                                            <Square size={15} />
                                                        </button>
                                                        <span>{task.text}</span>
                                                        {task.due && <span className="ml-auto text-xs text-amber-600 font-medium">Due {task.due}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* TASKS TAB */}
                            {activeDetailTab === 'tasks' && (
                                <div className="max-w-2xl space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            value={newTask}
                                            onChange={e => setNewTask(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && addTask()}
                                            placeholder="Add a task and press Enter…"
                                            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                                        />
                                        <button onClick={addTask} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-1">
                                            <Plus size={14} /> Add
                                        </button>
                                    </div>
                                    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                                        {selected.tasks.length === 0 ? (
                                            <p className="text-xs text-slate-400 italic p-5">No tasks yet</p>
                                        ) : selected.tasks.map(task => (
                                            <div key={task.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                                                <button onClick={() => toggleTask(task.id)} className={`shrink-0 transition-colors ${task.done ? 'text-emerald-500' : 'text-slate-300 hover:text-slate-500'}`}>
                                                    {task.done ? <CheckSquare size={16} /> : <Square size={16} />}
                                                </button>
                                                <span className={`flex-1 text-sm ${task.done ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                                                    {task.text}
                                                </span>
                                                {task.due && !task.done && (
                                                    <span className="text-xs text-amber-600 font-medium">Due {task.due}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* NOTES TAB */}
                            {activeDetailTab === 'notes' && (
                                <div className="max-w-2xl space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            value={newNote}
                                            onChange={e => setNewNote(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && addNote()}
                                            placeholder="Add a note and press Enter…"
                                            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                                        />
                                        <button onClick={addNote} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-1">
                                            <Plus size={14} /> Add
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {selected.notes.length === 0 ? (
                                            <p className="text-xs text-slate-400 italic">No notes yet</p>
                                        ) : selected.notes.map(note => (
                                            <div key={note.id} className="bg-white rounded-xl border border-slate-200 px-5 py-3.5">
                                                <p className="text-sm text-slate-800 leading-relaxed">{note.text}</p>
                                                <p className="text-xs text-slate-400 mt-2">{note.timestamp}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* OUTREACH TAB */}
                            {activeDetailTab === 'outreach' && (
                                <div className="max-w-2xl">
                                    <div className="bg-slate-900 rounded-xl overflow-hidden border border-slate-700">
                                        <div className="px-5 py-4 border-b border-slate-800 flex justify-between items-center">
                                            <h3 className="text-white font-bold text-sm flex items-center gap-2">
                                                <IconSparkles /> AI-Drafted Investor Update
                                            </h3>
                                            <button
                                                onClick={() => handleDraftEmail(selected)}
                                                disabled={isDrafting}
                                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
                                            >
                                                {isDrafting ? 'Writing…' : 'Draft Update'}
                                            </button>
                                        </div>
                                        <div className="p-5">
                                            {emailDraft ? (
                                                <>
                                                    <textarea
                                                        className="w-full h-64 bg-slate-800 text-slate-200 p-4 rounded-lg border border-slate-600 text-sm font-serif leading-relaxed outline-none focus:border-blue-500 mb-4 resize-none"
                                                        value={emailDraft}
                                                        onChange={e => setEmailDraft(e.target.value)}
                                                    />
                                                    <button
                                                        onClick={handleSend}
                                                        className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow transition-all"
                                                    >
                                                        Send to {selected.email}
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="h-32 flex flex-col items-center justify-center text-slate-500 text-sm italic">
                                                    <p>Click "Draft Update" to generate a personalized investor letter</p>
                                                    <p className="text-xs mt-1 text-slate-600">Pulls live portfolio P&L data automatically</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CRMView;
